import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useSubscription } from "../context/SubscriptionContext";
import { toast } from "sonner";
import {
  FloppyDisk, ArrowLeft, ArrowCounterClockwise, Eye, CircleNotch, Plus,
  TextT, VideoCamera, Palette, SquaresFour, TextAa, DeviceMobile, DeviceTablet,
  Monitor, ArrowUp, ArrowDown, CopySimple, Trash, TextItalic,
  TextAlignLeft, TextAlignCenter, TextAlignRight, CaretRight, ClipboardText,
} from "@phosphor-icons/react";
import LandingView from "../components/LandingView";
import {
  FONT_OPTIONS, WEIGHT_OPTIONS, SECTION_LABELS, SECTION_DEFAULT_WIDTHS,
  defaultBuilder, mergeBuilder, newSection, rid,
} from "../lib/builder";

const setIn = (obj, path, value) => {
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[path[path.length - 1]] = value;
  return clone;
};

const Field = ({ label, value, onChange, textarea, ...rest }) => (
  <label className="block">
    <span className="text-xs font-semibold text-zinc-400 mb-1 block">{label}</span>
    {textarea ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white focus:border-[#FFD60A] outline-none resize-y" {...rest} />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white focus:border-[#FFD60A] outline-none" {...rest} />
    )}
  </label>
);

const Range = ({ label, value, min, max, step = 1, onChange, suffix = "px" }) => (
  <label className="block">
    <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold text-zinc-400">{label}</span><span className="text-xs text-zinc-300 tabular-nums">{value}{suffix}</span></div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#FFD60A] cursor-pointer" />
  </label>
);

const SelectField = ({ label, value, options, onChange }) => (
  <label className="block">
    <span className="text-xs font-semibold text-zinc-400 mb-1 block">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#FFD60A]">
      {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  </label>
);

const ColorField = ({ label, value, onChange }) => (
  <label className="block">
    <span className="text-xs font-semibold text-zinc-400 mb-1 block">{label}</span>
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-10 rounded bg-transparent border border-white/10 cursor-pointer p-0.5" />
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="inherit" className="flex-1 rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#FFD60A]" />
    </div>
  </label>
);

const Card = ({ title, right, children }) => (
  <section className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
    <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm tracking-tight text-zinc-200">{title}</h3>{right}</div>
    {children}
  </section>
);

const TABS = [
  { id: "theme", label: "Theme", icon: Palette },
  { id: "sections", label: "Sections", icon: SquaresFour },
  { id: "text", label: "Text", icon: TextAa },
  { id: "content", label: "Content", icon: TextT },
];

const WIDTH_PRESETS = [
  { w: 414, icon: DeviceMobile, label: "Mobile" },
  { w: 834, icon: DeviceTablet, label: "Tablet" },
  { w: 1280, icon: Monitor, label: "Desktop" },
];

export default function AdminHomepage() {
  const navigate = useNavigate();
  const { sub } = useSubscription();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState("theme");
  const [sel, setSel] = useState({ path: null, section: null });
  const [clip, setClip] = useState(null);

  const paneRef = useRef(null);
  const contentRef = useRef(null);
  const [paneW, setPaneW] = useState(1200);
  const [contentH, setContentH] = useState(800);

  useEffect(() => {
    api.get("/site-content").then((r) => {
      const d = r.data || {};
      if (!d.builder) d.builder = defaultBuilder();
      d.builder = mergeBuilder(d);
      setData(d);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (sub && !sub.is_admin) setDenied(true); }, [sub]);

  const ready = !!data;
  useEffect(() => {
    if (!ready) return; const el = paneRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setPaneW(el.clientWidth)); ro.observe(el); setPaneW(el.clientWidth);
    return () => ro.disconnect();
  }, [ready]);
  useEffect(() => {
    if (!ready) return; const el = contentRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setContentH(el.offsetHeight)); ro.observe(el); setContentH(el.offsetHeight);
    return () => ro.disconnect();
  }, [ready]);

  const up = (path, value) => setData((d) => setIn(d, path, value));
  const mutate = (fn) => setData((d) => fn(structuredClone(d)));

  const onSelectText = (pstr) => { setSel({ path: pstr, section: null }); setTab("text"); };
  const onSelectSection = (id) => { setSel({ path: null, section: id }); setTab("sections"); };
  const toggleHidden = (skey) => mutate((d) => { const h = d.builder.hiddenSections || []; d.builder.hiddenSections = h.includes(skey) ? h.filter((x) => x !== skey) : [...h, skey]; return d; });

  const uploadAsset = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return fileUrl(res.data.storage_path);
  };

  const onCopy = (desc) => { setClip(desc); toast.success("Section copied to clipboard"); };
  const paste = () => { mutate((d) => {
    if (!clip) return d;
    if (clip.kind === "custom") { const obj = structuredClone(clip.obj); obj.id = rid("sec"); d.builder.extraSections.push(obj); d.builder.sectionOrder.push({ id: rid("ord"), type: "custom", sid: obj.id }); }
    else d.builder.sectionOrder.push({ id: rid("ord"), type: "curated", key: clip.key });
    return d;
  }); toast.success("Section pasted at the bottom"); };

  const addSection = (type) => {
    const ns = newSection(type);
    const ordId = rid("ord");
    mutate((d) => { d.builder.extraSections.push(ns); d.builder.sectionOrder.push({ id: ordId, type: "custom", sid: ns.id }); return d; });
    setSel({ path: null, section: ordId });
    setTab("sections");
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/site-content", { data }); toast.success("Homepage published!"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the homepage to the default design? Your edits will be lost.")) return;
    try { const res = await api.post("/site-content/reset"); const d = res.data.data; if (!d.builder) d.builder = defaultBuilder(); d.builder = mergeBuilder(d); setData(d); setSel({ path: null, section: null }); toast.success("Reset to default."); }
    catch (e) { toast.error("Reset failed."); }
  };

  if (denied) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#070707] text-white gap-4">
        <p className="text-zinc-400">Admins only.</p>
        <button onClick={() => navigate("/dashboard")} className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm">Back to dashboard</button>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#070707] text-white"><CircleNotch size={28} className="animate-spin" /></div>;

  const B = data.builder;
  const canvasWidth = B.canvasWidth || 1280;
  const scale = Math.min(1, (paneW - 40) / canvasWidth);

  // ---- selection resolution ----
  const fixedKey = (sel.section === "nav" || sel.section === "footer") ? sel.section : null;
  const entry = !fixedKey && sel.section ? B.sectionOrder.find((e) => e.id === sel.section) : null;
  const curatedKey = fixedKey || (entry?.type === "curated" ? entry.key : null);
  const customSid = entry?.type === "custom" ? entry.sid : null;
  const customIdx = customSid ? B.extraSections.findIndex((s) => s.id === customSid) : -1;
  const customObj = customIdx >= 0 ? B.extraSections[customIdx] : null;

  const secWidth = (k) => B.sectionWidths[k] ?? SECTION_DEFAULT_WIDTHS[k] ?? 1280;
  const setSecWidth = (k, v) => mutate((d) => { d.builder.sectionWidths[k] = v; return d; });
  const setSecStyle = (k, prop, v) => mutate((d) => { d.builder.sectionStyles[k] = { ...(d.builder.sectionStyles[k] || {}) }; if (v === "" || v == null) delete d.builder.sectionStyles[k][prop]; else d.builder.sectionStyles[k][prop] = v; return d; });
  const setExtra = (i, prop, v) => mutate((d) => { d.builder.extraSections[i][prop] = v; return d; });
  const moveOrder = (dir) => mutate((d) => { const o = d.builder.sectionOrder; const i = o.findIndex((e) => e.id === sel.section); if (i < 0) return d; const j = i + dir; if (j < 0 || j >= o.length) return d; [o[i], o[j]] = [o[j], o[i]]; return d; });
  const dupSel = () => mutate((d) => { const o = d.builder.sectionOrder; const i = o.findIndex((e) => e.id === sel.section); const e = o[i]; if (!e) return d; if (e.type === "custom") { const cp = structuredClone(d.builder.extraSections.find((s) => s.id === e.sid)); cp.id = rid("sec"); d.builder.extraSections.push(cp); o.splice(i + 1, 0, { id: rid("ord"), type: "custom", sid: cp.id }); } else o.splice(i + 1, 0, { id: rid("ord"), type: "curated", key: e.key }); return d; });
  const delSel = () => { mutate((d) => { const e = d.builder.sectionOrder.find((x) => x.id === sel.section); if (!e) return d; d.builder.sectionOrder = d.builder.sectionOrder.filter((x) => x.id !== sel.section); if (e.type === "custom" && !d.builder.sectionOrder.some((x) => x.type === "custom" && x.sid === e.sid)) d.builder.extraSections = d.builder.extraSections.filter((s) => s.id !== e.sid); return d; }); setSel({ path: null, section: null }); };

  // ---- text panel ----
  const pathArr = sel.path ? sel.path.split(".").map((k) => (/^\d+$/.test(k) ? Number(k) : k)) : null;
  const getByPath = (arr) => (arr ? arr.reduce((a, k) => (a == null ? a : a[k]), data) : "");
  const ts = (sel.path && B.textStyles[sel.path]) || {};
  const setText = (prop, value) => mutate((d) => {
    d.builder.textStyles = d.builder.textStyles || {};
    const cur = { ...(d.builder.textStyles[sel.path] || {}) };
    if (value == null || value === "" || value === 0) delete cur[prop]; else cur[prop] = value;
    if (Object.keys(cur).length) d.builder.textStyles[sel.path] = cur; else delete d.builder.textStyles[sel.path];
    return d;
  });
  const setGrad = (patch) => { const g = { angle: 90, start: "#FFD60A", end: "#ff7ac0", ...(ts.gradient || {}), ...patch }; setText("gradient", g); };
  const clearText = () => mutate((d) => { if (d.builder.textStyles) delete d.builder.textStyles[sel.path]; return d; });

  const orderLabel = (e) => e.type === "custom" ? `Custom ${B.extraSections.find((s) => s.id === e.sid)?.type || "section"}` : SECTION_LABELS[e.key];

  return (
    <div className="h-screen flex flex-col bg-[#070707] text-white font-body overflow-hidden">
      <header className="shrink-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="px-4 h-14 flex items-center justify-between">
          <button data-testid="admin-back-btn" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white"><ArrowLeft size={16} /> Dashboard</button>
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-sm hidden sm:block">Visual Page Builder</span>
            <div className="hidden md:flex items-center gap-1 ml-2 rounded-full bg-white/5 border border-white/10 p-0.5">
              {WIDTH_PRESETS.map((p) => { const Ico = p.icon; const active = canvasWidth === p.w; return (
                <button key={p.w} data-testid={`canvas-preset-${p.label.toLowerCase()}`} title={`${p.label} (${p.w}px)`} onClick={() => up(["builder", "canvasWidth"], p.w)} className={`h-7 w-8 rounded-full flex items-center justify-center ${active ? "bg-[#FFD60A] text-black" : "text-zinc-300 hover:text-white"}`}><Ico size={15} weight={active ? "fill" : "regular"} /></button>); })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-view-btn" onClick={() => window.open("/", "_blank")} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-white/5 text-sm text-zinc-300"><Eye size={15} /> <span className="hidden sm:inline">View</span></button>
            <button data-testid="admin-reset-btn" onClick={reset} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-white/5 text-sm text-zinc-300"><ArrowCounterClockwise size={15} /> <span className="hidden sm:inline">Reset</span></button>
            <button data-testid="admin-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full font-bold text-sm text-black disabled:opacity-60" style={{ backgroundColor: "#FFD60A" }}>{saving ? <CircleNotch size={15} className="animate-spin" /> : <FloppyDisk weight="fill" size={15} />} Publish</button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        <aside className="w-[340px] shrink-0 border-r border-white/10 bg-[#0B0B0B] flex flex-col">
          <div className="flex border-b border-white/10">
            {TABS.map((t) => { const Ico = t.icon; return (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex-1 flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${tab === t.id ? "text-[#FFD60A] border-b-2 border-[#FFD60A]" : "text-zinc-500 hover:text-zinc-300"}`}><Ico size={17} weight={tab === t.id ? "fill" : "regular"} /> {t.label}</button>); })}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {tab === "theme" && (
              <>
                <Card title="Branding">
                  <Field label="Brand name" value={data.brand} onChange={(v) => up(["brand"], v)} />
                  <ColorField label="Accent color" value={data.accent} onChange={(v) => up(["accent"], v)} />
                </Card>
                <Card title="Global typography">
                  <SelectField label="Display / heading font" value={data.fontDisplay || "Outfit"} options={FONT_OPTIONS} onChange={(v) => up(["fontDisplay"], v)} />
                  <SelectField label="Body font" value={B.fontBody || "Outfit"} options={FONT_OPTIONS} onChange={(v) => up(["builder", "fontBody"], v)} />
                </Card>
                <Card title="Canvas">
                  <Range label="Total canvas width" value={canvasWidth} min={360} max={1600} step={10} onChange={(v) => up(["builder", "canvasWidth"], v)} />
                  <p className="text-[11px] text-zinc-500">Maximum width of the whole page. Per-section widths live in the Sections tab.</p>
                </Card>
              </>
            )}

            {tab === "sections" && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="add-text-section" onClick={() => addSection("text")} className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-white/10 bg-black/30 hover:bg-white/5 text-sm font-semibold"><TextT size={15} /> Text block</button>
                  <button data-testid="add-media-section" onClick={() => addSection("media")} className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-white/10 bg-black/30 hover:bg-white/5 text-sm font-semibold"><VideoCamera size={15} /> Video block</button>
                </div>
                {clip && <button data-testid="paste-section" onClick={paste} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-[#FFD60A]/40 bg-[#FFD60A]/10 text-[#FFD60A] text-sm font-semibold"><ClipboardText size={15} /> Paste copied section</button>}

                {sel.section ? (
                  <Card title={customObj ? `${customObj.type === "media" ? "Video" : "Text"} section` : `${SECTION_LABELS[curatedKey] || curatedKey} section`}>
                    {curatedKey && (
                      <>
                        <Range label="Content width" value={secWidth(curatedKey)} min={320} max={1600} step={10} onChange={(v) => setSecWidth(curatedKey, v)} />
                        {curatedKey !== "nav" && curatedKey !== "footer" && <Range label="Vertical padding" value={B.sectionStyles[curatedKey]?.paddingY ?? 80} min={0} max={200} step={4} onChange={(v) => setSecStyle(curatedKey, "paddingY", v)} />}
                        <ColorField label="Background color" value={B.sectionStyles[curatedKey]?.bg} onChange={(v) => setSecStyle(curatedKey, "bg", v)} />
                        <label className="flex items-center justify-between pt-1"><span className="text-xs font-semibold text-zinc-400">Visible on page</span><input type="checkbox" checked={!(B.hiddenSections || []).includes(curatedKey)} onChange={() => toggleHidden(curatedKey)} className="accent-[#FFD60A] h-4 w-4" /></label>
                      </>
                    )}
                    {customObj && (
                      <>
                        <Range label="Content width" value={customObj.width ?? 1024} min={320} max={1600} step={10} onChange={(v) => setExtra(customIdx, "width", v)} />
                        <Range label="Vertical padding" value={customObj.paddingY ?? 64} min={0} max={200} step={4} onChange={(v) => setExtra(customIdx, "paddingY", v)} />
                        <ColorField label="Background color" value={customObj.bg} onChange={(v) => setExtra(customIdx, "bg", v)} />
                        {customObj.type === "media" ? (
                          <Field label="Video URL (YouTube / Vimeo / Facebook / MP4)" value={customObj.url || ""} onChange={(v) => setExtra(customIdx, "url", v)} placeholder="Paste a link, or use Upload MP4 on the block" />
                        ) : (
                          <>
                            <Field label="Heading" value={customObj.heading || ""} onChange={(v) => setExtra(customIdx, "heading", v)} />
                            <Field label="Body" textarea value={customObj.body || ""} onChange={(v) => setExtra(customIdx, "body", v)} />
                            <SelectField label="Alignment" value={customObj.align || "center"} options={[{ label: "Left", value: "left" }, { label: "Center", value: "center" }, { label: "Right", value: "right" }]} onChange={(v) => setExtra(customIdx, "align", v)} />
                          </>
                        )}
                      </>
                    )}
                    {entry && (
                      <div className="grid grid-cols-4 gap-1.5 pt-1">
                        <button data-testid="panel-move-up" onClick={() => moveOrder(-1)} className="py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold inline-flex items-center justify-center"><ArrowUp size={13} /></button>
                        <button data-testid="panel-move-down" onClick={() => moveOrder(1)} className="py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold inline-flex items-center justify-center"><ArrowDown size={13} /></button>
                        <button data-testid="panel-dup" onClick={dupSel} className="py-2 rounded-lg bg-[#FFD60A]/15 text-[#FFD60A] hover:bg-[#FFD60A]/25 text-xs font-semibold inline-flex items-center justify-center"><CopySimple size={13} /></button>
                        <button data-testid="panel-del" onClick={delSel} className="py-2 rounded-lg bg-rose-600/20 text-rose-300 hover:bg-rose-600/30 text-xs font-semibold inline-flex items-center justify-center"><Trash size={13} /></button>
                      </div>
                    )}
                    <button onClick={() => setSel({ path: null, section: null })} className="text-[11px] text-zinc-500 hover:text-zinc-300 pt-1">← Back to all sections</button>
                  </Card>
                ) : (
                  <Card title="Hero background video">
                    <label className="flex items-center justify-between"><span className="text-xs font-semibold text-zinc-400">Show video behind hero</span><input type="checkbox" checked={!!B.heroVideo?.show} onChange={(e) => up(["builder", "heroVideo", "show"], e.target.checked)} className="accent-[#FFD60A] h-4 w-4" /></label>
                    <Field label="Hero video URL" value={B.heroVideo?.url || ""} onChange={(v) => up(["builder", "heroVideo", "url"], v)} placeholder="YouTube / Vimeo / MP4" />
                  </Card>
                )}

                {!sel.section && (
                  <Card title="All sections">
                    <p className="text-[11px] text-zinc-500 -mt-1 mb-1">Hover any section in the preview for its Add / Duplicate / Copy / Delete toolbar, or pick one here.</p>
                    <button data-testid="seclist-nav" onClick={() => setSel({ path: null, section: "nav" })} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-zinc-300"><span className={(B.hiddenSections || []).includes("nav") ? "line-through text-zinc-600" : ""}>Navbar</span><CaretRight size={13} className="text-zinc-600" /></button>
                    {B.sectionOrder.map((e) => (
                      <button key={e.id} data-testid={`seclist-${e.type === "curated" ? e.key : e.sid}`} onClick={() => setSel({ path: null, section: e.id })} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-zinc-300">
                        <span className={`inline-flex items-center gap-1.5 ${e.type === "curated" && (B.hiddenSections || []).includes(e.key) ? "line-through text-zinc-600" : ""}`}>{e.type === "custom" && (B.extraSections.find((s) => s.id === e.sid)?.type === "media" ? <VideoCamera size={13} /> : <TextT size={13} />)}{orderLabel(e)}</span>
                        <CaretRight size={13} className="text-zinc-600" />
                      </button>
                    ))}
                    <button data-testid="seclist-footer" onClick={() => setSel({ path: null, section: "footer" })} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-zinc-300"><span className={(B.hiddenSections || []).includes("footer") ? "line-through text-zinc-600" : ""}>Footer</span><CaretRight size={13} className="text-zinc-600" /></button>
                  </Card>
                )}
              </>
            )}

            {tab === "text" && (
              sel.path ? (
                <Card title="Selected text">
                  <p className="text-[11px] text-zinc-500 -mt-1 break-all">{sel.path}</p>
                  <p className="text-[11px] text-zinc-500 bg-white/5 rounded-lg px-2.5 py-2 leading-relaxed">Tip: highlight specific words in the preview to color or bold just those words via the floating toolbar.</p>
                  <Field label="Content (plain text — clears word colors)" textarea value={(getByPath(pathArr) || "").replace(/<[^>]*>/g, "")} onChange={(v) => up(pathArr, v)} />
                  <SelectField label="Font family" value={ts.fontFamily || "Inherit"} options={["Inherit", ...FONT_OPTIONS]} onChange={(v) => setText("fontFamily", v === "Inherit" ? null : v)} />
                  <Range label={`Font size${ts.fontSize ? "" : " (auto)"}`} value={ts.fontSize || 0} min={0} max={160} step={1} onChange={(v) => setText("fontSize", v)} />
                  <SelectField label="Font weight" value={ts.fontWeight || ""} options={[{ label: "Inherit", value: "" }, ...WEIGHT_OPTIONS]} onChange={(v) => setText("fontWeight", v ? Number(v) : null)} />
                  <div className="flex items-center gap-2">
                    <button data-testid="text-italic" onClick={() => setText("italic", ts.italic ? null : true)} className={`h-9 w-9 rounded-lg border border-white/10 flex items-center justify-center ${ts.italic ? "bg-[#FFD60A] text-black" : "text-zinc-300 hover:bg-white/5"}`}><TextItalic size={16} /></button>
                    <div className="flex gap-1.5">{[{ a: "left", I: TextAlignLeft }, { a: "center", I: TextAlignCenter }, { a: "right", I: TextAlignRight }].map(({ a, I }) => (<button key={a} data-testid={`text-align-${a}`} onClick={() => setText("align", ts.align === a ? null : a)} className={`h-9 w-9 rounded-lg border border-white/10 flex items-center justify-center ${ts.align === a ? "bg-[#FFD60A] text-black" : "text-zinc-300 hover:bg-white/5"}`}><I size={16} /></button>))}</div>
                  </div>
                  <ColorField label="Text color" value={ts.color} onChange={(v) => setText("color", v)} />
                  <div className="rounded-lg border border-white/10 p-3 space-y-2">
                    <label className="flex items-center justify-between"><span className="text-xs font-bold text-zinc-300">Text gradient</span><input data-testid="grad-toggle" type="checkbox" checked={!!ts.gradient} onChange={(e) => setText("gradient", e.target.checked ? { angle: 90, start: "#FFD60A", end: "#ff7ac0" } : null)} className="accent-[#FFD60A] h-4 w-4" /></label>
                    {ts.gradient && (
                      <>
                        <ColorField label="Start color" value={ts.gradient.start} onChange={(v) => setGrad({ start: v })} />
                        <ColorField label="End color" value={ts.gradient.end} onChange={(v) => setGrad({ end: v })} />
                        <Range label="Angle" value={ts.gradient.angle ?? 90} min={0} max={360} step={5} suffix="°" onChange={(v) => setGrad({ angle: v })} />
                      </>
                    )}
                  </div>
                  <button onClick={clearText} className="w-full py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-zinc-300">Clear all overrides</button>
                </Card>
              ) : (
                <div className="text-center text-zinc-500 text-sm py-10 px-4"><TextAa size={28} className="mx-auto mb-3 opacity-50" />Click any text in the live preview to restyle it. Highlight words to color or bold them individually.</div>
              )
            )}

            {tab === "content" && (
              <>
                <Card title="Hero">
                  <Field label="Badge" value={data.hero.badge} onChange={(v) => up(["hero", "badge"], v)} />
                  <Field label="Title (line 1)" value={data.hero.titleTop} onChange={(v) => up(["hero", "titleTop"], v)} />
                  <Field label="Title accent" value={data.hero.titleAccent} onChange={(v) => up(["hero", "titleAccent"], v)} />
                  <Field label="Subtitle" textarea value={data.hero.subtitle} onChange={(v) => up(["hero", "subtitle"], v)} />
                  <Field label="Hero image URL" value={data.hero.image} onChange={(v) => up(["hero", "image"], v)} />
                </Card>
                <Card title="Pricing cards">
                  {data.pricing.map((p, i) => (
                    <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2"><Field label="Name" value={p.name} onChange={(v) => up(["pricing", i, "name"], v)} /><Field label="Price" value={p.price} onChange={(v) => up(["pricing", i, "price"], v)} /></div>
                      <Field label="Badge (blank = none)" value={p.badge} onChange={(v) => up(["pricing", i, "badge"], v)} />
                      <Field label="Features (one per line)" textarea value={p.features.join("\n")} onChange={(v) => up(["pricing", i, "features"], v.split("\n").filter(Boolean))} />
                    </div>
                  ))}
                  <Field label="Footnote" value={data.pricingFootnote} onChange={(v) => up(["pricingFootnote"], v)} />
                </Card>
                <Card title="FAQ">
                  {data.faq.map((f, i) => (<div key={i} className="rounded-lg border border-white/10 p-3 space-y-2"><Field label="Question" value={f.q} onChange={(v) => up(["faq", i, "q"], v)} /><Field label="Answer" textarea value={f.a} onChange={(v) => up(["faq", i, "a"], v)} /></div>))}
                </Card>
                <Card title="Final CTA">
                  <Field label="Title" value={data.finalCta.title} onChange={(v) => up(["finalCta", "title"], v)} />
                  <Field label="Subtitle" textarea value={data.finalCta.subtitle} onChange={(v) => up(["finalCta", "subtitle"], v)} />
                  <Field label="Button label" value={data.finalCta.cta} onChange={(v) => up(["finalCta", "cta"], v)} />
                </Card>
              </>
            )}
          </div>
        </aside>

        <main ref={paneRef} data-testid="builder-preview" className="flex-1 overflow-auto bg-[#050505] p-5 flex justify-center" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)", backgroundSize: "22px 22px" }}>
          <div style={{ width: canvasWidth * scale, height: contentH * scale }}>
            <div ref={contentRef} className="rounded-xl overflow-hidden shadow-2xl shadow-black ring-1 ring-white/10" style={{ width: canvasWidth, transform: `scale(${scale})`, transformOrigin: "top left" }}>
              <LandingView
                content={data}
                editable
                onChange={(path, value) => up(path, value)}
                onContent={mutate}
                onNavigate={() => {}}
                onUpload={uploadAsset}
                onCopy={onCopy}
                sel={sel}
                onSelectText={onSelectText}
                onSelectSection={onSelectSection}
                onToggleHidden={toggleHidden}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
